/**
 * The account's entitlement, as the server last reported it — cached on the
 * device, for display and the synchronous capture gate.
 *
 * The server computes entitlement per request and enforces it itself; nothing
 * here is trusted to grant anything. The cache exists so a screen can say
 * "Tally + AI, renews 1 Nov" offline and instantly, and capture can
 * read a lapse without awaiting the network (INV-4) — never so the client can
 * decide what it may do.
 *
 * Keyed by account: a cache written for one account must never be read for
 * another on a shared device, so a mismatched `accountId` reads as no cache.
 */

import { API_BASE } from "./api_base";
import { adoptSessionExpiry, readSession } from "./backend";

export const ENTITLEMENT_STORAGE_KEY = "aegae.entitlement.v1";

export type PlanId = "tally" | "tally_ai" | "tally_ai_plus" | "family";
export type EntitlementSource = "apple" | "google" | "paddle" | "comp" | "admin" | "family";
export type EntitlementState = "none" | "trial" | "active" | "grace" | "lapsed" | "comp_active" | "comp_expired";

export interface Pool {
  used: number;
  /** Null when the allowance is lifted (admin). */
  limit: number | null;
}

export interface Entitlement {
  ownSubscription?: OwnSubscription;
  duplicates?: readonly DuplicateSubscription[];
  familyPayerDisplayName?: string | null;
  plan: PlanId | null;
  /** apple / google / paddle / comp / admin / family, or null with no plan. */
  source: EntitlementSource | null;
  state: EntitlementState;
  billingInterval: "monthly" | "yearly" | null;
  readOnly: boolean;
  captureBlocked: boolean;
  periodEnd: string | null;
  lapseAt: string | null;
  cancelAtPeriodEnd: boolean;
  ai: {
    available: boolean;
    reason: string | null;
    periodEnd: string | null;
    documents: Pool;
    messages: Pool;
    paused: boolean;
  };
}

export interface OwnSubscription {
  plan: PlanId;
  source: "apple" | "google" | "paddle";
  state: "active" | "trial" | "grace";
  billingInterval: "monthly" | "yearly";
  readOnly: false;
  periodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface DuplicateSubscription {
  subscriptionId: string;
  provider: "apple" | "google" | "paddle";
  plan: PlanId;
}

function duplicateSubscriptions(raw: unknown): DuplicateSubscription[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) throw new Error("The account's duplicate subscriptions are malformed.");
  return raw.map(value => {
    const sub = record(value);
    if (typeof sub.subscription_id !== "string" || typeof sub.plan_id !== "string" || !PLANS.includes(sub.plan_id) ||
        !(sub.provider === "apple" || sub.provider === "google" || sub.provider === "paddle")) {
      throw new Error("The account's duplicate subscription is malformed.");
    }
    return { subscriptionId: sub.subscription_id, provider: sub.provider, plan: sub.plan_id as PlanId };
  });
}

function ownSubscription(raw: unknown): OwnSubscription | undefined {
  if (raw === undefined) return undefined;
  const sub = record(raw);
  if (typeof sub.plan !== "string" || !PLANS.includes(sub.plan) ||
      !(sub.source === "apple" || sub.source === "google" || sub.source === "paddle") ||
      !(sub.state === "active" || sub.state === "trial" || sub.state === "grace") ||
      !(sub.billing_interval === "monthly" || sub.billing_interval === "yearly") ||
      typeof sub.period_end !== "string" || !Number.isFinite(Date.parse(sub.period_end)) ||
      typeof sub.cancel_at_period_end !== "boolean") throw new Error("The account's own subscription is malformed.");
  return { plan: sub.plan as PlanId, source: sub.source, state: sub.state, billingInterval: sub.billing_interval,
    readOnly: false, periodEnd: sub.period_end, cancelAtPeriodEnd: sub.cancel_at_period_end };
}

export interface CachedEntitlement {
  accountId: string;
  etag: string;
  /** When this device last heard from the server. */
  fetchedAt: string;
  entitlement: Entitlement;
  /** The server's body as received, so a 304 can re-store it unchanged. */
  wire: unknown;
}

const PLANS: readonly string[] = ["tally", "tally_ai", "tally_ai_plus", "family"];
const STATES: readonly string[] = ["none", "trial", "active", "grace", "lapsed", "comp_active", "comp_expired"];

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function pool(raw: unknown): Pool {
  const p = record(raw);
  const used = p.used;
  const limit = p.limit;
  if (typeof used !== "number" || !(limit === null || typeof limit === "number")) {
    throw new Error("The entitlement's AI pool is malformed.");
  }
  return { used, limit };
}

/**
 * Read the server's wire shape. Throws on anything a screen would render
 * wrongly — an unknown plan or state is a server bug, reported as one rather
 * than drawn as a guess (INV-7).
 */
export function parseEntitlement(raw: unknown): Entitlement {
  const body = record(raw);
  const plan = body.plan;
  const state = body.state;
  const source = body.source;
  const billingInterval = body.billing_interval;
  if (!(source === null || source === "apple" || source === "google" || source === "paddle" || source === "comp" || source === "admin" || source === "family")) {
    throw new Error("The server reported an entitlement source this app does not know.");
  }
  if (!(billingInterval === null || billingInterval === "monthly" || billingInterval === "yearly")) {
    throw new Error("The server reported a billing interval this app does not know.");
  }
  if (!(plan === null || (typeof plan === "string" && PLANS.includes(plan)))) {
    throw new Error("The server reported a plan this app does not know.");
  }
  if (typeof state !== "string" || !STATES.includes(state)) {
    throw new Error("The server reported an entitlement state this app does not know.");
  }
  if (typeof body.read_only !== "boolean") throw new Error("The entitlement does not say whether it is read-only.");
  const ai = record(body.ai);
  if (typeof ai.available !== "boolean") throw new Error("The entitlement does not say whether the agent is available.");
  return {
    ...(body.own_subscription === undefined ? {} : { ownSubscription: ownSubscription(body.own_subscription) }),
    duplicates: duplicateSubscriptions(body.duplicates),
    familyPayerDisplayName: nullableString(body.family_payer_display_name),
    plan: plan as PlanId | null,
    source,
    state: state as EntitlementState,
    billingInterval,
    readOnly: body.read_only,
    captureBlocked: body.capture_blocked === true,
    periodEnd: nullableString(body.period_end),
    lapseAt: nullableString(body.lapse_at),
    cancelAtPeriodEnd: body.cancel_at_period_end === true,
    ai: {
      available: ai.available,
      reason: nullableString(ai.reason),
      periodEnd: nullableString(ai.period_end),
      documents: pool(ai.documents),
      messages: pool(ai.messages),
      paused: ai.paused === true,
    },
  };
}

/**
 * The cached entitlement for `accountId`, or null. A cache for another
 * account, an unreadable one or blocked storage all read as null — and an
 * unreadable one is logged and removed rather than quietly ignored forever.
 */
export function readCachedEntitlement(accountId: string): CachedEntitlement | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(ENTITLEMENT_STORAGE_KEY);
  } catch (cause) {
    console.warn("Could not read the cached entitlement:", cause);
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = record(JSON.parse(raw));
    if (parsed.accountId !== accountId) return null;
    if (typeof parsed.etag !== "string" || typeof parsed.fetchedAt !== "string") {
      throw new Error("missing etag or fetchedAt");
    }
    return {
      accountId,
      etag: parsed.etag,
      fetchedAt: parsed.fetchedAt,
      entitlement: parseEntitlement(parsed.entitlementWire),
      wire: parsed.entitlementWire,
    };
  } catch (cause) {
    console.warn("Discarding an unreadable cached entitlement:", cause);
    clearCachedEntitlement();
    return null;
  }
}

function writeCache(accountId: string, etag: string, fetchedAt: string, wire: unknown): void {
  try {
    localStorage.setItem(
      ENTITLEMENT_STORAGE_KEY,
      JSON.stringify({ accountId, etag, fetchedAt, entitlementWire: wire }),
    );
    window.dispatchEvent(new Event(ENTITLEMENT_CHANGED));
  } catch (cause) {
    // Storage full or blocked: the answer is still shown, just not kept.
    console.warn("Could not cache the entitlement:", cause);
  }
}

export function clearCachedEntitlement(): void {
  try {
    localStorage.removeItem(ENTITLEMENT_STORAGE_KEY);
    window.dispatchEvent(new Event(ENTITLEMENT_CHANGED));
  } catch (cause) {
    console.warn("Could not clear the cached entitlement:", cause);
  }
}

/**
 * Ask the server, sending the cached etag so an unchanged answer is a 304.
 * Rejects on a network failure or a bad answer; the caller keeps showing the
 * cache and says it could not refresh.
 */
export async function refreshEntitlement(
  now: () => Date = () => new Date(),
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<CachedEntitlement | null> {
  const session = readSession();
  if (session === null) return null;
  const cached = readCachedEntitlement(session.accountId);

  const response = await fetchImpl(`${API_BASE}/v1/entitlement`, {
    signal,
    headers: {
      authorization: `Bearer ${session.token}`,
      ...(cached === null ? {} : { "if-none-match": cached.etag }),
    },
  });
  adoptSessionExpiry(response);

  const fetchedAt = now().toISOString();
  if (response.status === 304 && cached !== null) {
    writeCache(session.accountId, cached.etag, fetchedAt, cached.wire);
    return { ...cached, fetchedAt };
  }
  if (!response.ok) throw new Error(`The server could not say what this account includes (HTTP ${String(response.status)}).`);

  const wire: unknown = await response.json();
  const entitlement = parseEntitlement(wire);
  const etag = response.headers.get("etag") ?? nullableString(record(wire).etag);
  if (etag === null) throw new Error("The server sent an entitlement without an etag.");
  writeCache(session.accountId, etag, fetchedAt, wire);
  return { accountId: session.accountId, etag, fetchedAt, entitlement, wire };
}

/** Capture consults only a recent, positive server answer synchronously (INV-4). */
export const ENTITLEMENT_FRESH_MS = 15 * 60 * 1000;
export function cachedCaptureBlocked(now = Date.now()): boolean {
  const session = readSession();
  if (!session) return false;
  const cache = readCachedEntitlement(session.accountId);
  return cache !== null && cache.entitlement.captureBlocked && now - Date.parse(cache.fetchedAt) <= ENTITLEMENT_FRESH_MS;
}
export const ENTITLEMENT_CHANGED = "tally-entitlement-changed";
